import React, { useEffect, useState, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const FREQUENCIES = [
  { key: 'PER_STAY', label: 'Per Stay' },
  { key: 'PER_NIGHT', label: 'Per Night' },
  { key: 'PER_ROOM_SERVICE', label: 'Per Room Service' },
];

const emptyForm = {
  item_code: '', item_name: '', category: '', unit: 'PCS',
  reorder_level: 0, par_stock_level: 0, standard_consumption_qty: 0,
  consumption_frequency: 'PER_STAY', automatic_consumption: false,
  supplier: '', purchase_rate: 0, is_active: true,
};

/**
 * Guest Amenity Item Master (docs/DEV_PMS_OPERATIONS_ENHANCEMENT.md Module 1).
 * Standard CRUD grid — add new items, edit existing ones inline. Items marked
 * "Auto Consume" never appear on the housekeeping app's confirm screen; they
 * post at their standard qty automatically when a cleaning task's consumption
 * is confirmed (see HkAmenityTemplates for the per-room-type qty mapping).
 */
function HkAmenityItems({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState(null); // null = not editing; 'NEW' = creating
  const [form, setForm] = useState(emptyForm);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    if (!token || !selectedProperty?.id) return;
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/v1/amenity-inventory/items?all=1', { headers: headers() });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) { setError(data.error || 'Failed to load items'); return; }
      setItems(data);
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally { setLoading(false); }
  }, [token, selectedProperty, headers]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditingId('NEW'); setForm(emptyForm); setSuccess(''); };
  const startEdit = (item) => { setEditingId(item.item_id); setForm({ ...item }); setSuccess(''); };
  const cancelEdit = () => { setEditingId(null); setForm(emptyForm); };

  const save = async () => {
    setError(''); setSuccess(''); setSaving(true);
    try {
      const isNew = editingId === 'NEW';
      const res = await fetch(isNew ? '/api/v1/amenity-inventory/items' : `/api/v1/amenity-inventory/items/${editingId}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: headers(),
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Failed to save item'); return; }
      setSuccess(isNew ? 'Item created.' : 'Item updated.');
      cancelEdit();
      load();
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally { setSaving(false); }
  };

  const toggleActive = async (item) => {
    try {
      await fetch(`/api/v1/amenity-inventory/items/${item.item_id}`, {
        method: 'PUT', headers: headers(), body: JSON.stringify({ is_active: !item.is_active }),
      });
      load();
    } catch { /* no-op */ }
  };

  if (!selectedProperty?.id) {
    return <div style={{ padding: 24, color: '#6b7280' }}>Select a property to manage amenity items.</div>;
  }

  return (
    <div style={{ padding: 24, maxWidth: 1080, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h2 style={{ margin: 0 }}>Guest Amenity Item Master</h2>
        {editingId == null && (
          <button onClick={startNew}
            style={{ padding: '7px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            + New Item
          </button>
        )}
      </div>
      <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
        Items consumed per room during cleaning (towels, water bottles, toiletries). Map standard
        quantities per room type in <b>Amenity Templates</b>, and see current on-hand stock in <b>Amenity Stock</b>.
      </p>

      {error && <div style={{ background: '#fde8e8', color: '#b71c1c', padding: '8px 12px', borderRadius: 4, marginBottom: 12, fontSize: 14 }}>{error}</div>}
      {success && <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '8px 12px', borderRadius: 4, marginBottom: 12, fontSize: 14 }}>{success}</div>}

      {editingId != null && (
        <div style={{ marginBottom: 20, padding: 16, background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Item Code">
              <input value={form.item_code} disabled={editingId !== 'NEW'} onChange={e => setForm({ ...form, item_code: e.target.value })} style={inputStyle} />
            </Field>
            <Field label="Item Name">
              <input value={form.item_name} onChange={e => setForm({ ...form, item_name: e.target.value })} style={inputStyle} />
            </Field>
            <Field label="Category">
              <input value={form.category || ''} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="e.g. Bathroom" style={inputStyle} />
            </Field>
            <Field label="Unit">
              <input value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} placeholder="PCS" style={inputStyle} />
            </Field>
            <Field label="Reorder Level">
              <input type="number" value={form.reorder_level} onChange={e => setForm({ ...form, reorder_level: Number(e.target.value) })} style={inputStyle} />
            </Field>
            <Field label="Par Stock Level">
              <input type="number" value={form.par_stock_level} onChange={e => setForm({ ...form, par_stock_level: Number(e.target.value) })} style={inputStyle} />
            </Field>
            <Field label="Standard Consumption Qty">
              <input type="number" value={form.standard_consumption_qty} onChange={e => setForm({ ...form, standard_consumption_qty: Number(e.target.value) })} style={inputStyle} />
            </Field>
            <Field label="Consumption Frequency">
              <select value={form.consumption_frequency} onChange={e => setForm({ ...form, consumption_frequency: e.target.value })} style={inputStyle}>
                {FREQUENCIES.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </Field>
            <Field label="Supplier">
              <input value={form.supplier || ''} onChange={e => setForm({ ...form, supplier: e.target.value })} style={inputStyle} />
            </Field>
            <Field label="Purchase Rate">
              <input type="number" value={form.purchase_rate} onChange={e => setForm({ ...form, purchase_rate: Number(e.target.value) })} style={inputStyle} />
            </Field>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.automatic_consumption} onChange={e => setForm({ ...form, automatic_consumption: e.target.checked })} />
            <span style={{ fontSize: 14 }}>Auto-consume — posts automatically at the template quantity, never shown to housekeeping staff for review</span>
          </label>
          <div style={{ marginTop: 14, display: 'flex', gap: 10 }}>
            <button onClick={save} disabled={saving}
              style={{ padding: '8px 24px', background: saving ? '#999' : '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={cancelEdit} style={{ padding: '8px 20px', background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer', fontSize: 14 }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? <p>Loading…</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Unit</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Auto Consume</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Active</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.item_id} style={{ opacity: it.is_active ? 1 : 0.55 }}>
                  <td style={tdStyle}>{it.item_code}</td>
                  <td style={tdStyle}>{it.item_name}</td>
                  <td style={tdStyle}>{it.category || '—'}</td>
                  <td style={tdStyle}>{it.unit}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{it.automatic_consumption ? 'Yes' : '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <input type="checkbox" checked={it.is_active} onChange={() => toggleActive(it)} />
                  </td>
                  <td style={tdStyle}>
                    <button onClick={() => startEdit(it)} style={{ border: 'none', background: 'none', color: '#1976d2', cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No amenity items yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</label>
    {children}
  </div>
);

const thStyle = { padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #ddd', whiteSpace: 'nowrap' };
const tdStyle = { padding: '8px', borderBottom: '1px solid #e0e0e0' };
const inputStyle = { width: '100%', padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13.5, boxSizing: 'border-box' };

export default HkAmenityItems;
